import { useMemo, useCallback } from 'react';
import { useApolloClient, useQuery } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';
import { useEntityAri } from '@atlassian/jira-business-entity-common/src/controllers/ari-context/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { CategorySelectOption, CategoryFieldConfig } from '../../common/types.tsx';
import type {
	JwmCategoryOptions,
	JwmCategoryOptionsVariables,
	JwmCategoryOptions_jira_jwmCategoryOptions_JiraWorkManagementCategoryOptionConnection_edges as JwmCategoryOptionsEdges,
} from './__generated_apollo__/JwmCategoryOptions';
import { ITEMS_PER_PAGE } from './constants.tsx';
import { CATEGORY_OPTIONS } from './gql.tsx';
import { transformCategoryOptionsData } from './utils.tsx';

export type CategoryServiceOutput = {
	categoryOptions: CategorySelectOption[];
	loading: boolean;
	error: Error | null | undefined;
	called?: boolean;
};

export const useCategoryOptionsService = (): CategoryServiceOutput => {
	const entityAri = useEntityAri();

	const { data, loading, error } = useQuery<JwmCategoryOptions, JwmCategoryOptionsVariables>(
		CATEGORY_OPTIONS,
		{
			variables: {
				projectId: entityAri,
				first: ITEMS_PER_PAGE,
			},
			onError: (apolloError: ApolloError) => {
				fireErrorAnalytics({
					meta: {
						id: 'jwmCategoryOptions',
						packageName: 'jiraBusinessCategories',
						teamName: 'jira-warepil',
					},
					attributes: {
						message: 'Failed to fetch category options with color',
					},
					error:
						Array.isArray(apolloError) && apolloError.length > 0
							? apolloError[0]
							: // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								(apolloError as ApolloError),
					sendToPrivacyUnsafeSplunk: true,
				});
			},
		},
	);

	const options = useMemo(
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		() => transformCategoryOptionsData(data as JwmCategoryOptions | null | undefined),
		[data],
	);

	return {
		categoryOptions: options,
		loading,
		error,
	};
};

export const useCategoryOptionsCache = () => {
	const analyticsTaskName = 'useCategoryOptionsCacheApolloCacheActions';
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const client = useApolloClient();
	const entityAri = useEntityAri();

	const getNewTotalCount = useCallback(
		(currentCount: number | null, action: 'REMOVE' | 'ADD' | 'UPDATE') => {
			if (currentCount && action === 'ADD') {
				return currentCount + 1;
			}
			if (currentCount && action === 'REMOVE') {
				return currentCount - 1;
			}
			return currentCount;
		},
		[],
	);

	const categoryOptionsCache = useCallback(
		(category: CategoryFieldConfig, action: 'REMOVE' | 'ADD' | 'UPDATE') => {
			const getCachedListData = () => {
				try {
					const read = client.readQuery({
						query: CATEGORY_OPTIONS,
						variables: {
							projectId: entityAri,
							first: ITEMS_PER_PAGE,
						},
					});

					fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloCacheRead taskSuccess', {
						task: analyticsTaskName,
					});

					return read;
				} catch (e) {
					fireErrorAnalytics({
						error: e instanceof Error ? e : new Error('Unknown cache read error'),
						meta: {
							id: 'categoryOptionsCache',
							packageName: 'jiraBusinessCategories',
							teamName: 'jira-warepil',
						},
						sendToPrivacyUnsafeSplunk: true,
						skipSentry: true,
						attributes: {
							task: analyticsTaskName,
							query: JSON.stringify(CATEGORY_OPTIONS),
							variables: {
								projectId: entityAri,
								first: ITEMS_PER_PAGE,
							},
							error: JSON.stringify(e),
							eventName: 'apolloCacheRead taskFail',
						},
					});

					return null;
				}
			};

			const data: JwmCategoryOptions | null = getCachedListData();

			if (data == null || data.jira?.jwmCategoryOptions?.__typename === 'QueryError') {
				return;
			}

			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const edges = data.jira?.jwmCategoryOptions
				?.edges as ReadonlyArray<JwmCategoryOptionsEdges | null> | null;
			if (edges == null) {
				return;
			}

			const newEdges: (JwmCategoryOptionsEdges | null)[] = [...edges];
			const index = newEdges.findIndex(
				(edge) => edge && edge.node?.option?.optionId === category.option.optionId,
			);

			switch (action) {
				case 'ADD':
					newEdges.push({
						__typename: 'JiraWorkManagementCategoryOptionEdge',
						node: {
							__typename: 'JiraWorkManagementCategoryOption',
							id: category.id || '',
							color: category.color || null,
							option: {
								id: category.option.id || '',
								optionId: category.option.optionId,
								value: category.option.value || '',
								isDisabled: false,
								__typename: 'JiraOption',
							},
						},
					});
					break;
				case 'REMOVE':
					if (index !== -1) {
						newEdges.splice(index, 1);
					}
					break;
				case 'UPDATE':
					if (index !== -1) {
						const edgeToUpdate = newEdges[index];
						if (edgeToUpdate) {
							const edgeUpdated = {
								...edgeToUpdate,
								node: edgeToUpdate?.node && {
									...edgeToUpdate.node,
									option: edgeToUpdate.node.option && {
										...edgeToUpdate?.node?.option,
										value: category.option.value || '',
									},
									color: category.color || null,
								},
							};
							newEdges.splice(index, 1, edgeUpdated);
						}
					}
					break;
				default:
					return;
			}

			const totalCount = data.jira?.jwmCategoryOptions?.totalCount || null;
			const newTotalCount = getNewTotalCount(totalCount, action);

			const newData: JwmCategoryOptions = {
				...data,
				jira: data.jira && {
					...data.jira,
					jwmCategoryOptions:
						data?.jira?.jwmCategoryOptions?.__typename ===
						'JiraWorkManagementCategoryOptionConnection'
							? {
									...data.jira.jwmCategoryOptions,
									edges: newEdges,
									totalCount: newTotalCount,
								}
							: null,
				},
			};
			try {
				client.writeQuery({
					query: CATEGORY_OPTIONS,
					variables: {
						projectId: entityAri,
						first: ITEMS_PER_PAGE,
					},
					data: newData,
				});
				fireOperationalAnalytics(createAnalyticsEvent({}), 'apolloCacheWrite taskSuccess', {
					task: analyticsTaskName,
				});
			} catch (e) {
				fireErrorAnalytics({
					error: e instanceof Error ? e : new Error('Unknown cache write error'),
					meta: {
						id: 'categoryOptionsCache',
						packageName: 'jiraBusinessCategories',
						teamName: 'jira-warepil',
					},
					sendToPrivacyUnsafeSplunk: true,
					skipSentry: true,
					attributes: {
						task: analyticsTaskName,
						query: JSON.stringify(CATEGORY_OPTIONS),
						variables: {
							projectId: entityAri,
							first: ITEMS_PER_PAGE,
						},
						eventName: 'apolloCacheWrite taskFail',
						error: JSON.stringify(e),
					},
				});
			}
		},
		[client, createAnalyticsEvent, entityAri, getNewTotalCount],
	);

	return useMemo(
		() => ({
			addCategory(category: CategoryFieldConfig) {
				categoryOptionsCache(category, 'ADD');
			},
			removeCategory(optionId: string) {
				categoryOptionsCache({ option: { optionId }, color: null }, 'REMOVE');
			},
			updateCategory(category: CategoryFieldConfig) {
				categoryOptionsCache(category, 'UPDATE');
			},
		}),
		[categoryOptionsCache],
	);
};
